import { AsyncPipe, DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { Subscription } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { LaboralClass } from 'src/app/models/CardEnrichmentRequest';
import { FeasibilityRule } from 'src/app/models/FeasibilityRule';
import { LaborRegimeRequest } from 'src/app/models/LaborRegimeRequest';
import { NewFeasibilityStatus } from 'src/app/models/NewFeasibilityStatus';
import { D4A_MOCK, DNI_MOCK, LABORAL_REGIME_AUTONOMO_MOCK, NUM_ANOS_EMPRESA_ACTUAL_MOCK } from 'src/app/shared/constants/freelancer-feasibility.constants';
import { environment } from 'src/environments/environment';
import { FreelancerRequestDetailService } from '../freelancer-request-detail.service';
import { FreelancerRequestDetailFeasibilityService } from './freelancer-request-detail-feasibility.service';

@Component({
    selector: 'app-freelancer-request-detail-feasibility',
    templateUrl: './freelancer-request-detail-feasibility.component.html',
    styleUrl: './freelancer-request-detail-feasibility.component.scss',
    standalone: true,
    imports: [
        NgClass,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        InputNumberModule,
        InputTextModule,
        NgFor,
        TranslateModule,
        SelectModule,
        RadioButtonModule,
        DecimalPipe,
        AsyncPipe
    ]
})
export class FreelancerRequestDetailFeasibilityComponent implements OnDestroy {

    private rulesSubscription: Subscription | any;
	@Input() newFeasibilityStatus: NewFeasibilityStatus = { title: 'New feasibility', step: 1 };
	readonly OBRules = ['CodigoReglaOB1', 'CodigoReglaOB2'];
	requestMap: Map<string, any>;
	isCalculating = false;

	constructor(
		private readonly requestDetailService: FreelancerRequestDetailService,
		public readonly requestDetailFeasibilityService: FreelancerRequestDetailFeasibilityService,
		private readonly toastService: ToastService,
		private readonly translateService: TranslateService,
		private readonly confirmationService: ConfirmationService,
		private readonly decimalPipe: DecimalPipe,
        private readonly apiService: ApiService
	) { }

	ngOnDestroy(): void {
	    if (this.rulesSubscription) {
	        this.rulesSubscription.unsubscribe();
	    }
	}

	async submit() {
	    if (this.requestDetailFeasibilityService.feasibilityForm.invalid) {
	        this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.SOME_FIELDS_REQUIRED', true);
	        return;
	    }

	    this.isCalculating = true;
	    this.requestMap = await this.createRequestMap();
	    this.rulesSubscription = this.requestDetailFeasibilityService.getRules().subscribe(rules => {
	        this.requestDetailFeasibilityService.isFeasible = this.evaluateRules(rules, this.requestMap);
	    });

	    this.newFeasibilityStatus = { title: 'Feasibility result', step: 2 };
	    this.isCalculating = false;
	}

	goToStepOne(): void {
	    this.newFeasibilityStatus = { title: 'New feasibility', step: 1 };
	}

	goToStepTwo(): void {
	    this.newFeasibilityStatus = { title: 'Feasibility result', step: 2 };
	}

	calculateFreelancerFeasibilityResult(): void {
	    this.newFeasibilityStatus = { title: 'Feasibility status', step: 3 };
	}

	completeOperation(): void {
	    this.confirmationService.confirm({
	        message: this.translateService.instant('ADMINISTRATION.FEASIBILITY.CONFIRMATION_MESSAGE'),
	        acceptLabel: this.translateService.instant('BUTTON.YES'),
	        rejectLabel: this.translateService.instant('BUTTON.NO'),
	        accept: () => {
	            if (this.requestDetailFeasibilityService.isFeasible && this.requestDetailFeasibilityService.freelancerFeasibilityResult.ok === true) {
	                this.requestDetailFeasibilityService.grantCredit().catch(error => { console.error(error); });
	            }
	            else {
	                this.requestDetailFeasibilityService.denyCredit().catch(error => { console.error(error); });
	            }
	        }
	    });
	}

	private async createRequestMap(): Promise<Map<string, any>> {
	    const data = this.requestDetailService.request.data;
	    const score = data['Score'];
	    const calculos = data['Calculos'];
	    let laboralRegime: string;
	    const facturacionValue: number = calculos['Facturacion'];;
	    let odNumAnoEmpActualValue: number;
	    const odIngresosValue: number = calculos['OD_Ingresos'];
	    let D4AValue: number;

	    if (environment.production){
	        try {
	            laboralRegime = await this.getLaborRegime();
	            odNumAnoEmpActualValue = calculos['OD_NUMANOEMPACTUAL'];
	            D4AValue = score['puntuacion'];
	        } catch (ex) {
	            this.toastService.showError('TOAST.ERROR', 'CARDS.CALCULATED_FIELDS_ERROR', true);
	            console.error(ex);
	        }
	    } else {
	        if (this.requestDetailService.request.personQuery.personId === DNI_MOCK){
	            laboralRegime = LABORAL_REGIME_AUTONOMO_MOCK;
	            D4AValue = 999;
	        } else {
	            laboralRegime = await this.getLaborRegime();
	            D4AValue = D4A_MOCK;
	        }

	        // facturacionValue = FACTURACION_MOCK;
	        odNumAnoEmpActualValue = NUM_ANOS_EMPRESA_ACTUAL_MOCK;
	        // odIngresosValue = INGRESOS_MOCK;
	    }

	    try {
	        await this.requestDetailFeasibilityService.calculateCredit(odIngresosValue, this.requestDetailFeasibilityService.feasibilityForm.get('IngresosRecurrentesTrimestrales').value);
	    } catch (err) {
	        this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.CALCULATE_FEASIBILITY_ERROR', true);
	        console.error(err);
	    }

	    return new Map([
	        /* CAMPOS PERSONQUERY */
	        ["id", this.requestDetailService.request.personQuery.personId],
	        ["personType", this.requestDetailService.request.personQuery.personType],

	        /* CAMPOS ADICIONALES CALCULADOS */
	        ["Facturacion", facturacionValue],
	        ["OD_NumAnoEmpActual", odNumAnoEmpActualValue],
	        ["OD_Ingresos", odIngresosValue],
	        ["D4A", D4AValue],

	        /* CAMPOS FORMULARIO */
	        ["IngresosElegibles", this.requestDetailFeasibilityService.feasibilityForm.get('IngresosElegibles').value],
	        ["GastosComprometidos", this.requestDetailFeasibilityService.feasibilityForm.get('GastosComprometidos').value],
	        ["ScoreOpenBanking", this.requestDetailFeasibilityService.feasibilityForm.get('ScoreOpenBanking').value],
	        ["ImporteDeudaTotalCirculanteCIRBE", this.requestDetailFeasibilityService.feasibilityForm.get('ImporteDeudaTotalCirculanteCIRBE').value],
	        ["ImporteMoraCIRBE", this.requestDetailFeasibilityService.feasibilityForm.get('ImporteMoraCIRBE').value],
	        ["IngresosRecurrentesTrimestrales", this.requestDetailFeasibilityService.feasibilityForm.get('IngresosRecurrentesTrimestrales').value],
	        ['ViabilidadOpenBanking', this.requestDetailFeasibilityService.feasibilityForm.get('ViabilidadOpenBanking').value],
	        ['RegimenLaboral', laboralRegime],
	        ['IngresosRecurrentesTrimestralesBool', this.requestDetailFeasibilityService.freelancerFeasibilityResult.ok]
	    ]);
	}

	private async getLaborRegime(): Promise<string> {
	    const LaborRegime: LaborRegimeRequest = {
	        queryId: this.requestDetailService.request.personQuery.personId,
	        laboral: this.requestDetailService.request.data['Laboral'] as LaboralClass
	    };
	    return await this.apiService.post<string>('requests/labor-regime', LaborRegime);
	}

	private evaluateRules(rules: FeasibilityRule[], requestMap: Map<string, any>): boolean {
	    this.requestDetailFeasibilityService.ruleFeasibilityResults = [];
	    let isFeasibleGlobal = true;
	    for (const rule of rules) {
	        if (!rule.active) {
	            continue;
	        }
	        const matchingRequest = requestMap.get(rule.fieldName);
	        const matchingRequest2 = rule.fieldName2 != null ? requestMap.get(rule.fieldName2) : null;
	        const isFeasible = this.requestDetailFeasibilityService.validateRule(rule, { value: matchingRequest, value2: matchingRequest2 });

	        this.requestDetailFeasibilityService.ruleFeasibilityResults.push({
	            ruleConcept: rule.ruleConcept,
	            ruleDescription: rule.ruleDescription,
	            fieldName: rule.fieldName,
	            fieldName2: rule.fieldName2,
	            operator: rule.operator,
	            value: rule.value,
	            active: rule.active,
	            isFeasible: isFeasible,
	            translationLabel: rule.translationLabel,
	            translationVersion: rule.translationVersion
	        });
	        if (!isFeasible) {
	            isFeasibleGlobal = false;
	        }
	    }
	    return isFeasibleGlobal;
	}

	transformValueToLocaleString(value: number): string {
	    return this.decimalPipe.transform(value, '1.0-2', 'es-ES');
	}
}
